/**
 * nexuscodeApi.js — Cliente HTTP + WebSocket para NEXUS CODE.
 *
 * Completamente isolado do Kaiso:
 *  - Prefixo /api/nexuscode/*
 *  - Token armazenado em localStorage com chave "nc_token" (separado do Kaiso)
 *  - Erros normalizados → { data, error }
 *
 * Resolução de URL (prioridade):
 *  1. REACT_APP_NC_API_URL  — nexuscode-backend standalone (produção nexuscodes.org)
 *  2. REACT_APP_BACKEND_URL — backend partilhado (dev local / staging)
 *  3. ""                    — proxy relativo (Render rewrite /api/nexuscode/*)
 */
import axios from "axios";

const _rawUrl = (
  process.env.REACT_APP_NC_API_URL   ||  // standalone nexuscode-backend
  process.env.REACT_APP_BACKEND_URL  ||  // kaiso backend compartilhado (dev)
  ""
).replace(/\/$/, "");

// Render fromService property:host retorna apenas o hostname sem protocolo
const BASE_URL = _rawUrl && !_rawUrl.startsWith("http")
  ? `https://${_rawUrl}`
  : _rawUrl;

const NC_PREFIX = `${BASE_URL}/api/nexuscode`;
const TOKEN_KEY = "nc_token";

// ── Axios instance isolado ────────────────────────────────────────────────────
const http = axios.create({
  baseURL: NC_PREFIX,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

http.interceptors.request.use(config => {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (_) {}
  return config;
});

http.interceptors.response.use(
  res => ({ data: res.data, error: null }),
  err => ({ data: null, error: err?.response?.data?.detail || err?.message || "Erro" }),
);

// ── Token helpers ─────────────────────────────────────────────────────────────
export const ncToken = {
  get:    ()      => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } },
  set:    (t)     => { try { localStorage.setItem(TOKEN_KEY, t); } catch (_) {} },
  remove: ()      => { try { localStorage.removeItem(TOKEN_KEY); } catch (_) {} },
  exists: ()      => !!ncToken.get(),
};

// ── Auth ──────────────────────────────────────────────────────────────────────
export const ncAuth = {
  register: (email, password, name) =>
    http.post("/auth/register", { email, password, name }),
  login:    (email, password) =>
    http.post("/auth/login", { email, password }),
  me:       () => http.get("/auth/me"),
  refresh:  () => http.post("/auth/refresh"),
};

// ── Workspace (GitHub config) ─────────────────────────────────────────────────
export const ncWorkspace = {
  get:  ()     => http.get("/workspace"),
  save: (body) => http.post("/workspace", body),
};

// ── Conversations ─────────────────────────────────────────────────────────────
export const ncConversations = {
  list:         (limit = 30) => http.get("/conversations", { params: { limit } }),
  create:       (title)      => http.post("/conversations", { title }),
  getMessages:  (convId)     => http.get(`/conversations/${convId}/messages`),
  getExecutions:(convId)     => http.get(`/conversations/${convId}/executions`),
};

// ── Execute ───────────────────────────────────────────────────────────────────
export const ncExecute = {
  run: (user_input, conv_id) => http.post("/execute", { user_input, conv_id }),
};

// ── Write approvals ───────────────────────────────────────────────────────────
export const ncApprovals = {
  approve: (id) => http.post(`/approve/${id}`),
  reject:  (id) => http.post(`/reject/${id}`),
};

// ── Billing ───────────────────────────────────────────────────────────────────
export const ncBilling = {
  credits: () => http.get("/billing/credits"),
  plans:   () => http.get("/billing/plans"),
};

// ── WebSocket ─────────────────────────────────────────────────────────────────
const WS_BASE = BASE_URL
  .replace(/^https?:\/\//, m => m === "https://" ? "wss://" : "ws://")
  || `ws://${window.location.host}`;

export function createNcSocket(convId, handlers = {}) {
  const url = `${WS_BASE}/api/nexuscode/ws/${convId}`;
  const ws  = new WebSocket(url);

  ws.onopen    = ()       => handlers.onOpen?.();
  ws.onclose   = (e)      => handlers.onClose?.(e);
  ws.onerror   = (e)      => handlers.onError?.(e);
  ws.onmessage = ({ data }) => {
    try   { handlers.onEvent?.(JSON.parse(data)); }
    catch { handlers.onRaw?.(data); }
  };

  return {
    send:  (payload)  => ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify(payload)),
    close: ()         => ws.close(),
    get readyState()  { return ws.readyState; },
  };
}
